import { useEffect, useRef, useState } from "react";
import { Calendar } from "lucide-react";
import { formatDisplayDate, parseDisplayDate } from "../utils/date";

export function DateField({ value, onChange, placeholder, showWeekday = false, ariaLabel, min }) {
  const [text, setText] = useState(() => formatDisplayDate(value, { weekday: showWeekday }));
  const nativeRef = useRef(null);
  const focused = useRef(false);
  const display = (iso) => formatDisplayDate(iso, { weekday: showWeekday });
  const hint = placeholder ?? (showWeekday ? "Sunday, DD Month YYYY" : "DD Month YYYY");

  useEffect(() => {
    if (focused.current) return;
    setText(display(value));
  }, [value, showWeekday]);

  function commit(next) {
    const iso = parseDisplayDate(next);
    if (iso && iso !== value) onChange?.(iso);
  }

  function handleBlur() {
    focused.current = false;
    const iso = parseDisplayDate(text);
    if (iso) {
      setText(display(iso));
      commit(text);
    } else {
      setText(display(value));
    }
  }

  function openPicker() {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through to focus */
      }
    }
    el.focus();
  }

  return (
    <span className="date-field">
      <input
        type="text"
        value={text}
        placeholder={hint}
        aria-label={ariaLabel || placeholder}
        onChange={(event) => {
          setText(event.target.value);
          commit(event.target.value);
        }}
        onFocus={() => { focused.current = true; }}
        onBlur={handleBlur}
      />
      <input
        ref={nativeRef}
        type="date"
        className="date-field-native"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ""}
        min={min}
        onChange={(event) => onChange?.(event.target.value)}
      />
      <button type="button" className="date-field-btn" onClick={openPicker} title="Pick a date" aria-label="Pick a date">
        <Calendar size={15} />
      </button>
    </span>
  );
}
